/** Workspace sections → gestão substages (table sub-sections). */
export type WorkspaceSectionKey = 'por_contactar' | 'em_tratamento' | 'pre_fecho' | 'fecho';

export type WorkspaceSubsection = {
  substage: string;
  label: string;
};

export type WorkspaceSection = {
  key: WorkspaceSectionKey;
  title: string;
  accent: string;
  subsections: WorkspaceSubsection[];
};

export const WORKSPACE_SECTIONS: WorkspaceSection[] = [
  {
    key: 'por_contactar',
    title: 'Por contactar',
    accent: '#dc2626',
    subsections: [
      { substage: 'por-contactar-iniciado-atribuido', label: 'Iniciado/Atribuído' },
      { substage: 'por-contactar-nao-atendido', label: 'Não Atendido' },
      { substage: 'por-contactar-incontactavel', label: 'Incontactável' },
      { substage: 'por-contactar-responsavel-ausente', label: 'Responsável Ausente' },
      { substage: 'por-contactar-callback', label: 'Callback' },
      { substage: 'por-contactar-cliente-edp', label: 'Cliente EDP' },
      { substage: 'por-contactar-sem-interesse', label: 'Sem Interesse' },
      { substage: 'por-contactar-duplicado-ou-invalido', label: 'Duplicado ou Inválido' },
    ],
  },
  {
    key: 'em_tratamento',
    title: 'Em Tratamento',
    accent: '#8b5cf6',
    subsections: [
      { substage: 'em-tratamento-aguardar-fatura', label: 'A aguardar Fatura' },
      { substage: 'em-tratamento-aguardar-proposta-bo', label: 'A aguardar Proposta BO' },
      { substage: 'em-tratamento-sem-interesse', label: 'Perdido' },
      { substage: 'em-tratamento-pendente', label: 'Pendente' },
      { substage: 'em-tratamento-fidelizados', label: 'Fidelizados' },
    ],
  },
  {
    key: 'pre_fecho',
    title: 'Pré-Fecho',
    accent: '#2563eb',
    subsections: [
      { substage: 'pre-fecho-proposta-enviada', label: 'Proposta Enviada' },
      { substage: 'pre-fecho-assinado', label: 'Assinatura' },
    ],
  },
  {
    key: 'fecho',
    title: 'Fecho',
    accent: '#10b981',
    subsections: [
      { substage: 'fecho-pendente-documentos', label: 'Pendente Documentos' },
      { substage: 'fecho-contrato-submetido', label: 'Contrato Submetido' },
    ],
  },
];

/** Substage where CCC submits contracts (Gestão das Marcações → Contrato Submetido). */
export const SUBSTAGE_CONTRATO_SUBMETIDO = 'fecho-contrato-submetido';

export const ALL_WORKSPACE_SUBSTAGES = WORKSPACE_SECTIONS.flatMap((s) =>
  s.subsections.map((sub) => sub.substage)
);

/** Default substage when a marcação is claimed (Por contactar → Iniciado/Atribuído). */
export const SUBSTAGE_AGENDAMENTOS = 'por-contactar-iniciado-atribuido';

/** Legacy Comercial substages still stored on some rows — shown under Por contactar. */
export const LEGACY_POR_CONTACTAR_SUBSTAGES: Record<string, string> = {
  'agendamentos-agendamentos': 'Iniciado/Atribuído',
  'agendamentos-remarcacao': 'Não Atendido',
  'reuniao-realizada-reuniao': 'Incontactável',
};

/** Legacy feedback substages — display labels under Em Tratamento. */
export const LEGACY_EM_TRATAMENTO_SUBSTAGES: Record<string, string> = {
  'feedback-pendencia-not-ok-cliente': 'Perdido',
  'feedback-pendencia-not-ok-callcenter': 'A aguardar Proposta BO',
  'feedback-pendencia-pendente': 'Pendente',
  'feedback-pendencia-concluido-sem-proposta': 'Perdido',
};
/** After Solicitar Proposta — moves row to Pré-Fecho → Proposta Enviada. */
export const SUBSTAGE_CONCLUIDO_COM_PROPOSTA = 'pre-fecho-proposta-enviada';
/** After successful Solicitar Proposta request — waits for BackOffice proposal. */
export const SUBSTAGE_AGUARDAR_PROPOSTA_BO = 'em-tratamento-aguardar-proposta-bo';

/** Legacy Pré-Fecho substages — display labels. */
export const LEGACY_PRE_FECHO_SUBSTAGES: Record<string, string> = {
  'feedback-pendencia-solicitar-proposta': 'Proposta Enviada',
  'feedback-pendencia-concluido-com-proposta': 'Assinatura',
};

/** Legacy resultado substages — display labels under Fecho. */
export const LEGACY_FECHO_SUBSTAGES: Record<string, string> = {
  'resultado-fecho-ok': 'Contrato Submetido',
  'resultado-fecho-not-ok': 'Pendente Documentos',
  'resultado-aguardar': 'Pendente Documentos',
  'resultado-cancelado': 'Pendente Documentos',
};

export function sectionForSubstage(substage: string): WorkspaceSection | undefined {
  const normalized = normalizeWorkspaceSubstage(substage);
  return WORKSPACE_SECTIONS.find((sec) => sec.subsections.some((s) => s.substage === normalized));
}

export function subsectionLabel(substage: string): string {
  for (const sec of WORKSPACE_SECTIONS) {
    const sub = sec.subsections.find((s) => s.substage === substage);
    if (sub) return sub.label;
  }
  if (LEGACY_POR_CONTACTAR_SUBSTAGES[substage]) {
    return LEGACY_POR_CONTACTAR_SUBSTAGES[substage];
  }
  if (LEGACY_EM_TRATAMENTO_SUBSTAGES[substage]) {
    return LEGACY_EM_TRATAMENTO_SUBSTAGES[substage];
  }
  if (LEGACY_PRE_FECHO_SUBSTAGES[substage]) {
    return LEGACY_PRE_FECHO_SUBSTAGES[substage];
  }
  if (LEGACY_FECHO_SUBSTAGES[substage]) {
    return LEGACY_FECHO_SUBSTAGES[substage];
  }
  return substage;
}

const LEGACY_SUBSTAGE_TO_BUCKET: Record<string, string> = {
  'agendamentos-agendamentos': 'por-contactar-iniciado-atribuido',
  'agendamentos-remarcacao': 'por-contactar-nao-atendido',
  'reuniao-realizada-reuniao': 'por-contactar-incontactavel',
  'feedback-pendencia-not-ok-cliente': 'em-tratamento-sem-interesse',
  'feedback-pendencia-not-ok-callcenter': 'em-tratamento-aguardar-proposta-bo',
  'feedback-pendencia-pendente': 'em-tratamento-pendente',
  'feedback-pendencia-concluido-sem-proposta': 'em-tratamento-sem-interesse',
  'feedback-pendencia-solicitar-proposta': 'pre-fecho-proposta-enviada',
  'feedback-pendencia-concluido-com-proposta': 'pre-fecho-assinado',
  'pre-fecho-aguardar-assinatura-cliente': 'pre-fecho-assinado',
  // Retired statuses: collapse into Assinatura for existing rows.
  'pre-fecho-fidelizado-f': 'pre-fecho-assinado',
  'pre-fecho-perdido-sem-assinatura': 'pre-fecho-assinado',
  'resultado-fecho-ok': 'fecho-contrato-submetido',
  'resultado-fecho-not-ok': 'fecho-pendente-documentos',
  'resultado-aguardar': 'fecho-pendente-documentos',
  'resultado-cancelado': 'fecho-pendente-documentos',
};

/** Map legacy substages into current workspace buckets for tables and navigation. */
export function normalizeWorkspaceSubstage(substage: string): string {
  return LEGACY_SUBSTAGE_TO_BUCKET[substage] || substage;
}
